import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useLazyQuery } from "@apollo/react-hooks";
import {
  Typography,
  Box,
  Grid,
  makeStyles,
  Paper,
  IconButton,
  SvgIcon,
  Divider
} from "@material-ui/core";
import {
  Covid19TestTypeFilters,
  FormActionDrawer,
  Loader,
  LocationCard,
  MapboxMap
} from "../../../components";
import { useHeader } from "../../../contexts";
import { GET_LOCATIONS_NEAR_ME } from "../../../queries";
import { MapboxLocationAutocomplete } from "../../../components/Common/MapboxLocationAutocomplete";
import { Marker } from "react-map-gl";
import { ReactComponent as LocationPinIcon } from "../../../images/location-pin.svg";
import Geocoding from "@mapbox/mapbox-sdk/services/geocoding";
import {
  CONSUMER_SESSION_LOCATION,
  COUNTRIES_ISO_CODES
} from "../../../constants";
import { useTranslation } from "react-i18next";
import { isTestService } from "../../../utils";
import moment from "moment";
import { Pagination } from "@material-ui/lab";

const LOCATIONS_PER_PAGE = 20;

const useClasses = makeStyles(theme => ({
  mapContainer: {
    position: "relative",
    height: "150px"
  },
  iconButton: {
    position: "relative"
  },
  iconOrder: {
    position: "absolute",
    left: "50%",
    transform: "translate(-50%, -90%)",
    top: "50%",
    fontSize: "12px",
    fontWeight: 700,
    color: theme.palette.common.black
  },
  locationPin: {
    fill: theme.palette.primary.main
  }
}));

const DEFAULT_MAP_CONFIG = {
  latitude: 32.779167,
  longitude: -96.808891,
  zoom: 8
};

const DEFAULT_COUNTRIES = [COUNTRIES_ISO_CODES.US, COUNTRIES_ISO_CODES.MX];

const getCurrentPosition = options =>
  new Promise((res, rej) =>
    navigator.geolocation.getCurrentPosition(res, rej, options)
  );

const ScheduleLocation = ({
  setDrawerClearance,
  updateScheduleData,
  scheduleData,
  isSubmitting
}) => {
  const locationCardRefs = useRef([]);
  const scrollToCardIndex = useRef();

  const { t } = useTranslation();
  const classes = useClasses();
  const { setTitle } = useHeader();
  const [selectedLocation, setSelectedLocation] = useState();
  const [preselectedLocation, setPreselectedLocation] = useState();
  const [error, setError] = useState(false);
  const [mapConfig, setMapConfig] = useState();
  const [locations, setLocations] = useState([]);
  const [activeFilter, setActiveFilter] = useState();
  const [currentPage, setCurrentPage] = useState(0);

  const geocodingClient = Geocoding({
    accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
  });

  useEffect(() => {
    setTitle(t("schedule.location.title"));
  }, []);

  const getCountries = selectedCountry =>
    selectedCountry && COUNTRIES_ISO_CODES[selectedCountry]
      ? [COUNTRIES_ISO_CODES[selectedCountry]]
      : DEFAULT_COUNTRIES;

  /**
   * Set map config to user location on mount
   */
  useEffect(() => {
    if (scheduleData.patientAddress) {
      geocodingClient
        .forwardGeocode({
          query: Object.values(scheduleData.patientAddress).join(","),
          limit: 1,
          countries: getCountries(scheduleData.country)
        })
        .send()
        .then(response => {
          const { body } = response;
          const { features = [] } = body;
          const [location] = features;

          if (location && Array.isArray(location.center)) {
            const [lng, lat] = location.center;

            setMapConfig({
              ...DEFAULT_MAP_CONFIG,
              latitude: lat,
              longitude: lng
            });
          }
        });
    } else {
      getCurrentPosition({ maximumAge: 0, timeout: 5000 })
        .then(({ coords }) => {
          setMapConfig({
            ...DEFAULT_MAP_CONFIG,
            latitude: coords.latitude,
            longitude: coords.longitude
          });
        })
        .catch(() => setMapConfig(DEFAULT_MAP_CONFIG));
    }
  }, []);

  /**
   * Get locations by active filter
   */
  const filteredLocations = activeFilter
    ? locations.filter(activeFilter.filterFn)
    : locations;

  /**
   * Paginated locations
   */
  const paginatedLocations = filteredLocations.filter(
    (location, index) =>
      location &&
      index >= currentPage * LOCATIONS_PER_PAGE &&
      index < currentPage * LOCATIONS_PER_PAGE + LOCATIONS_PER_PAGE
  );

  /**
   * Get location by id
   *
   * @param {number} id
   * @param {array} locations
   */
  const getLocationById = (id, locations) => {
    const location = locations.filter(location => location.id === parseInt(id));

    return location.length > 0 ? location[0] : {};
  };

  /**
   * Set location if we already have one
   */
  useEffect(() => {
    if (scheduleData.location) setSelectedLocation(scheduleData.location);
  }, [scheduleData]);

  /**
   * Set location id from URL
   */
  useEffect(() => {
    const savedLocation = sessionStorage.getItem(CONSUMER_SESSION_LOCATION);

    if (!locations || !savedLocation) return;

    const location = getLocationById(savedLocation, locations);

    if (location.id) {
      setPreselectedLocation(location);
      setSelectedLocation(location);
    }
  }, [locations]);

  /**
   * Get locations from API
   */
  const [getLocations, { loading, error: getLocationsError }] = useLazyQuery(
    GET_LOCATIONS_NEAR_ME,
    {
      onCompleted: ({ locations = {} }) => {
        if (!locations) return;

        const { available_location = [] } = locations;

        if (Array.isArray(available_location))
          // Set non-external location only
          setLocations(
            available_location.filter(location => !location.external)
          );
      },
      fetchPolicy: "no-cache"
    }
  );

  /**
   * Get locations when map config changes
   */
  useEffect(() => {
    if (!mapConfig) return;

    getLocations({
      variables: {
        groupCode: scheduleData.groupCode,
        date: scheduleData.date || moment().format("YYYY-MM-DD"),
        longitude: mapConfig.longitude,
        latitude: mapConfig.latitude,
        distance: 50
      }
    });
  }, [mapConfig]);

  /**
   * Scroll to location when page is updated
   * Handles scenarios where a pin is clicked and we're not on that page
   */
  useLayoutEffect(() => {
    if (scrollToCardIndex.current === undefined) return;

    scrollToLocationByIndex(scrollToCardIndex.current);

    scrollToCardIndex.current = undefined;
  }, [currentPage]);

  const handleSelectLocation = location => setSelectedLocation(location);

  const handleUpdateLocation = location => {
    const [lng, lat] = location.center;

    if (!lng || !lat) return;

    setMapConfig({ ...DEFAULT_MAP_CONFIG, latitude: lat, longitude: lng });
  };

  /**
   * Scroll to location card by index
   *
   * @param {number} index
   */
  const scrollToLocationByIndex = index => {
    if (locationCardRefs.current[index]) {
      locationCardRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  };

  /**
   * Handle filter change
   *
   * @param {Object} filter
   * @param {string} filter.label
   * @param {*} filter.filerFn
   */
  const handleFilterChange = filter => {
    setActiveFilter(filter);
    setCurrentPage(0);
  };

  /**
   * Handle pin click
   *
   * @param {number} index
   */
  const handleClickPin = index => {
    // Get page that the clicked pin is on
    const pageIndex = Math.ceil((parseInt(index) + 1) / LOCATIONS_PER_PAGE) - 1;

    // Get the adjusted index of the pin (0 - LOCATIONS_PER_PAGE)
    const indexAdjusted = index - LOCATIONS_PER_PAGE * pageIndex;

    // If not viewing page that index is on, save index and update current page
    if (pageIndex !== currentPage) {
      scrollToCardIndex.current = indexAdjusted;
      setCurrentPage(pageIndex);
    } else {
      scrollToLocationByIndex(indexAdjusted);
    }
  };

  const handleNext = () => {
    if (selectedLocation && selectedLocation.id) {
      // Remove previously stored
      sessionStorage.removeItem(CONSUMER_SESSION_LOCATION);

      updateScheduleData({
        location: selectedLocation.id,
        locationServices: selectedLocation.services_available || [],
        locationState: selectedLocation.address_state || "",
        collectInsurance:
          "collect_insurance_info" in selectedLocation &&
          selectedLocation.collect_insurance_info === false
            ? false
            : true,
        multipleTestTypesAvailable:
          selectedLocation.services_available.filter(service =>
            isTestService([service.service_code])
          ).length > 1
      });
    } else {
      setError(true);
    }
  };

  if (loading || !mapConfig) return <Loader />;
  if (getLocationsError)
    return (
      <Typography color="error">{t("schedule.location.error_api")}</Typography>
    );

  return (
    <Box data-cy="schedule-screen-location">
      {error && (
        <Typography
          paragraph
          data-cy="schedule-screen-location-error"
          color="error"
        >
          {t("schedule.location.error")}
        </Typography>
      )}
      <Grid container direction="row">
        <Grid item xs={12} className={classes.mapContainer}>
          <MapboxMap {...mapConfig} scrollZoom={false}>
            {filteredLocations.map((location, index) => (
              <Marker
                key={`marker-location-${location.id}`}
                latitude={location.lat}
                longitude={location.lng}
                offsetTop={-24}
                offsetLeft={-28}
              >
                <IconButton
                  className={classes.iconButton}
                  onClick={() => handleClickPin(index)}
                >
                  <SvgIcon fontSize="large">
                    <LocationPinIcon className={classes.locationPin} />
                  </SvgIcon>
                  <Box className={classes.iconOrder}>{index + 1}</Box>
                </IconButton>
              </Marker>
            ))}
          </MapboxMap>
        </Grid>
        <Grid item xs={12}>
          <Box py={2}>
            <Paper>
              <Box p={2}>
                <Box mb={2}>
                  <MapboxLocationAutocomplete
                    onUpdateLocation={handleUpdateLocation}
                    label={t(
                      "components.mapbox_location_autocomplete.form.address.label"
                    )}
                    geocodeConfig={{
                      types: ["address", "poi", "place"],
                      countries: getCountries(scheduleData.country)
                    }}
                  />
                </Box>
                <Typography variant="h6">
                  <strong>{t("schedule.location.results.title")}</strong>
                </Typography>
                <Typography paragraph>
                  {t("schedule.location.results.count", {
                    count: locations.length
                  })}
                </Typography>
                <Typography variant="h6" gutterBottom>
                  <strong>Filter by test type</strong>
                </Typography>
                <Covid19TestTypeFilters
                  onChange={filter => handleFilterChange(filter)}
                />
              </Box>
            </Paper>
          </Box>
          <Grid container spacing={2}>
            {preselectedLocation && (
              <Grid item xs={12} data-cy="schedule-screen-location-preselected">
                <LocationCard
                  location={preselectedLocation}
                  selectedLocation={preselectedLocation}
                  handleSelect={handleSelectLocation}
                  showMap={false}
                  showSelectedAlert={true}
                  servicesListFilter={service =>
                    isTestService([service.service_code])
                  }
                />
                <Box mt={2}>
                  <Typography align="center" variant="h6" gutterBottom>
                    <strong>{t("schedule.location.preselected.title")}</strong>
                  </Typography>
                  <Divider />
                </Box>
              </Grid>
            )}
            {paginatedLocations.map((location, index) => (
              <Grid
                key={location.id}
                item
                xs={12}
                ref={cardRef => (locationCardRefs.current[index] = cardRef)}
              >
                <LocationCard
                  location={location}
                  selectedLocation={selectedLocation}
                  handleSelect={handleSelectLocation}
                  showMap={false}
                  order={index + 1 + currentPage * LOCATIONS_PER_PAGE}
                  servicesListFilter={service =>
                    isTestService([service.service_code])
                  }
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <Pagination
                color="primary"
                variant="outlined"
                count={Math.ceil(filteredLocations.length / LOCATIONS_PER_PAGE)}
                page={currentPage + 1}
                onChange={(e, value) => setCurrentPage(value - 1)}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {selectedLocation && (
        <FormActionDrawer
          setDrawerClearance={setDrawerClearance}
          handleNext={() => handleNext()}
          loading={isSubmitting}
        />
      )}
    </Box>
  );
};

export { ScheduleLocation };
